// security/qrScanner.js
// BuzzInApp — Scanner de QR con cámara (jsQR)
//
// Expone:
//   startScanner(videoEl, canvasEl, onResult, onError)  → inicia el scan loop
//   stopScanner(stream)                                  → libera la cámara
//
// onResult recibe el string crudo del QR (se verifica en qrGenerator.js)
// Se usa la cámara trasera por defecto (environment facing)

import jsQR from "jsqr";

// ─── Estado interno del loop ──────────────────────────────────────────────────
let animationFrameId = null;
let scanning         = false;

// ─── Iniciar scanner ──────────────────────────────────────────────────────────

/**
 * Inicia el scanner de QR.
 *
 * @param {HTMLVideoElement}  videoEl   Elemento <video> donde se muestra la cámara
 * @param {HTMLCanvasElement} canvasEl  Canvas oculto para capturar frames
 * @param {(qrString: string) => void} onResult  Callback cuando se detecta un QR válido
 * @param {(error: Error) => void}     onError   Callback en caso de error de cámara
 * @returns {Promise<MediaStream|null>}  El stream para poder detenerlo después
 */
export const startScanner = async (videoEl, canvasEl, onResult, onError) => {
  scanning = true;

  // 1. Pedir acceso a la cámara (trasera preferida)
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" }, // cámara trasera
        width:      { ideal: 1280 },
        height:     { ideal: 720 },
      },
      audio: false,
    });
  } catch (err) {
    const message =
      err.name === "NotAllowedError"
        ? "camera_permission_denied"
        : "camera_not_available";
    onError(Object.assign(new Error(message), { original: err }));
    return null;
  }

  // 2. Conectar stream al elemento <video>
  videoEl.srcObject = stream;
  videoEl.setAttribute("playsinline", true); // iOS requiere esto
  await videoEl.play();

  // 3. Loop de detección
  const ctx = canvasEl.getContext("2d", { willReadFrequently: true });

  const tick = () => {
    if (!scanning) return;

    if (videoEl.readyState === videoEl.HAVE_ENOUGH_DATA) {
      // Ajustar canvas al tamaño actual del video
      canvasEl.height = videoEl.videoHeight;
      canvasEl.width  = videoEl.videoWidth;

      ctx.drawImage(videoEl, 0, 0, canvasEl.width, canvasEl.height);

      const imageData = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
      const code      = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (code && code.data) {
        // QR detectado — detenemos el loop para no disparar múltiples veces
        scanning = false;
        cancelAnimationFrame(animationFrameId);
        onResult(code.data);
        return;
      }
    }

    animationFrameId = requestAnimationFrame(tick);
  };

  animationFrameId = requestAnimationFrame(tick);
  return stream;
};

// ─── Detener scanner ──────────────────────────────────────────────────────────

/**
 * Detiene el scanner y libera la cámara.
 * @param {MediaStream|null} stream  El stream devuelto por startScanner
 */
export const stopScanner = (stream) => {
  scanning = false;
  cancelAnimationFrame(animationFrameId);

  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
};

// ─── Check de disponibilidad ──────────────────────────────────────────────────

/**
 * Verifica si el dispositivo tiene cámara disponible.
 * Útil para mostrar un mensaje alternativo en dispositivos sin cámara.
 * @returns {Promise<boolean>}
 */
export const isCameraAvailable = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return false;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((d) => d.kind === "videoinput");
  } catch {
    return false;
  }
};
